import React, { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { TIMELINE_ROUTE } from '../DepositPayoutTimeline/core/consts'
import { PageTitle } from '../../../../../_metronic/layout/core'
import { useDashboardPolling } from './components/useDashboardPolling'
import HealthBanner from './components/HealthBanner'
import KpiBadges from './components/KpiBadges'
import BasketsChart from './components/BasketsChart'
import CoverageRing from './components/CoverageRing'
import PoolsBreakdown from './components/PoolsBreakdown'
import TimelineChart from './components/TimelineChart'
import ShortfallChart from './components/ShortfallChart'
import MerchantsBlock from './components/MerchantsBlock'
import RefreshSelect from './components/RefreshSelect'
import CurrencySelect from '../../components/common/CurrencySelect'
import DrilldownModal from './components/DrilldownModal'
import MetricsModal from './components/MetricsModal'
import V2Block from './components/V2Block'
import { CURRENCY_STORAGE_KEY, DEFAULT_CURRENCY, METRICS_QUERY_PARAM } from './core/consts'
import { useChartTheme } from './core/chartTheme'
import { AlertItem } from './core/types'

// PageTitle keys its effect on the breadcrumbs prop identity, and PageDataProvider
// does not memoise its context value — so a fresh [] on every render re-fires the
// effect, updates provider state and re-renders this page, which makes another [].
// Hoisting the constant breaks that loop (CPAYMENT-1689).
const NO_BREADCRUMBS: never[] = []

const MatchingDashboardPage: React.FC = () => {
  const theme = useChartTheme()
  const { data, error, loading, refreshing, lastUpdated, intervalMs, setIntervalMs, refresh } =
    useDashboardPolling()
  const [drill, setDrill] = useState<{
    signal: string
    title: string
    currency?: string
    merchant?: number
  } | null>(null)
  // The metrics modal lives in the URL so the view can be linked and reopened;
  // the dashboard itself stays a single route.
  const [searchParams, setSearchParams] = useSearchParams()
  const metricsOpen = searchParams.get(METRICS_QUERY_PARAM) === '1'
  const setMetricsOpen = (open: boolean) => {
    const next = new URLSearchParams(searchParams)
    if (open) next.set(METRICS_QUERY_PARAM, '1')
    else next.delete(METRICS_QUERY_PARAM)
    setSearchParams(next, { replace: true })
  }

  const [currency, setCurrencyState] = useState<string>(
    () => localStorage.getItem(CURRENCY_STORAGE_KEY) || DEFAULT_CURRENCY
  )
  const setCurrency = (c: string) => {
    localStorage.setItem(CURRENCY_STORAGE_KEY, c)
    setCurrencyState(c)
  }

  const currencies = useMemo(() => (data?.currencies ?? []).map((c) => c.currency), [data])

  // Keep selection valid: if the stored currency isn't present, fall back.
  useEffect(() => {
    if (currencies.length && !currencies.includes(currency)) {
      setCurrencyState(currencies.includes(DEFAULT_CURRENCY) ? DEFAULT_CURRENCY : currencies[0])
    }
  }, [currencies, currency])

  const view = data?.currencies?.find((c) => c.currency === currency) ?? data?.currencies?.[0]

  // Banner spans ALL currencies (+ service alerts) so a problem in a non-selected
  // currency is never hidden. Defensive against contract skew during deploys.
  const allAlerts: AlertItem[] = useMemo(() => {
    if (!data) return []
    const perCur = (data.currencies ?? []).flatMap((c) => c.alerts ?? [])
    return [...perCur, ...(data.service_alerts ?? [])]
  }, [data])

  const openDrilldown = (
    signal: string,
    title: string,
    filters?: { currency?: string; merchant?: number }
  ) =>
    setDrill({
      signal,
      title,
      currency: filters?.currency ?? currency,
      merchant: filters?.merchant,
    })

  return (
    <>
      <PageTitle breadcrumbs={NO_BREADCRUMBS}>Состояние мэтчинга</PageTitle>

      <div className='d-flex justify-content-end mb-3'>
        <div className='position-relative d-flex align-items-center gap-4'>
          <Link to={TIMELINE_ROUTE} className='btn btn-sm btn-light-primary'>
            Таймлайн депозитов к выплатам
          </Link>
          <CurrencySelect currencies={currencies} value={currency} onChange={setCurrency} />
          <RefreshSelect
            intervalMs={intervalMs}
            setIntervalMs={setIntervalMs}
            lastUpdated={lastUpdated}
            onRefresh={refresh}
          />
          {refreshing && (
            <div
              className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded'
              style={{ background: theme.overlayBg, zIndex: 5 }}
            >
              <span className='spinner-border spinner-border-sm text-primary' role='status' />
            </div>
          )}
        </div>
      </div>

      {loading && !data && <div className='text-muted'>Загрузка…</div>}
      {error && !data && <div className='alert alert-danger'>{error}</div>}

      {data && (
        <>
          <HealthBanner
            severity={data.overall_severity}
            alerts={allAlerts}
            onOpenDrilldown={openDrilldown}
            onOpenMetrics={() => setMetricsOpen(true)}
          />

          {view ? (
            <>
              <KpiBadges kpis={view.kpis} onOpenDrilldown={openDrilldown} />

              {data.circuit === 'v2' ? (
                <div className='row g-5 mb-5'>
                  <div className='col-12 col-xl-8'>
                    {view.v2 && <V2Block block={view.v2} currency={view.currency} />}
                  </div>
                  <div className='col-12 col-xl-4'>
                    <CoverageRing coverage={view.coverage} currency={view.currency} />
                  </div>
                </div>
              ) : (
                <div className='row g-5 mb-5'>
                  <div className='col-12 col-xl-8'>
                    <BasketsChart baskets={view.baskets} onOpenDrilldown={openDrilldown} />
                  </div>
                  <div className='col-12 col-xl-4'>
                    <CoverageRing coverage={view.coverage} currency={view.currency} />
                  </div>
                </div>
              )}

              <PoolsBreakdown pools={view.pools} currency={view.currency} />

              <MerchantsBlock currency={view.currency} refreshToken={lastUpdated ?? 0} />

              <TimelineChart currency={view.currency} />

              <ShortfallChart currency={view.currency} />
            </>
          ) : (
            <div className='text-muted'>Нет активной статистики мэтчинга</div>
          )}
        </>
      )}

      <MetricsModal show={metricsOpen} kpis={view?.kpis} onClose={() => setMetricsOpen(false)} />

      <DrilldownModal
        signal={drill?.signal ?? null}
        title={drill?.title}
        currency={drill?.currency}
        merchant={drill?.merchant}
        onClose={() => setDrill(null)}
      />
    </>
  )
}

export default MatchingDashboardPage
